/**
 * The two cells in a lead row that are more than a value.
 *
 * Split out of `LeadsTable` when the row learned to open the scan report: the
 * table file was already at the size where a reader has to scroll past two
 * hundred lines of cell markup to find out what a click does. The table now
 * owns the grid, the sorting and the paging; these two own their own problem.
 *
 * Both sit UNDER the row's stretched overlay, so every link in here lifts
 * itself with `relative z-[1]`. Without it the anchor is drawn, is blue, and
 * silently opens the scan report like the rest of the row.
 */
import { Link } from "react-router-dom";
import { Building2, ExternalLink } from "lucide-react";
import { cn } from "@wa/ui";
import type { ScanLead } from "../../../lib/scanLeads";
import { Pop } from "../../shell/Pop";
import { StatusPill } from "../../ui";
import type { T } from "../site-scanner/types";

/**
 * Why this site is a lead.
 *
 * These are sentences, not tags: "WordPress 6.1.10 is a major version behind
 * (latest 7.0.2)" is four times the width of the track it sits in. Two of them
 * wrapped inside the pill row turned an 62px row into a 200px one and pushed
 * everything else in the row off its own baseline - the whole point of the pill
 * design being that a row is one line you scan.
 *
 * So the cell shows ONE reason, on one line, cut where the column ends, and the
 * rest go behind a counter that opens them. The first reason is the one the
 * scorer ranked first, so the truncated line is still the headline rather than
 * an arbitrary pick.
 */
export function Reasons({ lead, t }: { lead: ScanLead; t: T }) {
  const reasons = lead.reasons ?? [];
  const first = reasons[0];
  if (!first) return <span className="crm-mut text-[12.5px]">-</span>;

  const text = (r: (typeof reasons)[number]) => t(`leads.reason.${r.id}`, r.vars ?? {});
  const rest = reasons.slice(1);

  return (
    <span className="flex min-w-0 items-center gap-1.5">
      {/* The ellipsis needs a block to sit in: `crm-chip` is an inline-flex
          box, and `text-overflow` on a flex container never draws. */}
      <span className="crm-chip min-w-0" title={text(first)}>
        <span className="crm-trunc block">{text(first)}</span>
      </span>
      {rest.length ? (
        <span className="relative z-[1]">
          <Pop
            wide
            trigger={(open) => (
              <button
                type="button"
                className={cn("crm-cb crm-cb-wide crm-cb-sm", open && "is-on")}
                aria-haspopup="menu"
                aria-expanded={open}
                aria-label={t("leads.reasonsMore", { n: rest.length })}
              >
                +{rest.length}
              </button>
            )}
          >
            <div className="crm-menu-head">
              <div className="crm-menu-t">{t("leads.col.reasons")}</div>
              <div className="crm-menu-s">{lead.companyName ?? lead.domain}</div>
            </div>
            {/* Read-only rows, so no `crm-mi`: a menu item that cannot be pressed
                is a control the keyboard has to walk through for nothing. */}
            <ul className="crm-reasons">
              {reasons.map((r) => (
                <li key={r.id}>{text(r)}</li>
              ))}
            </ul>
          </Pop>
        </span>
      ) : null}
    </span>
  );
}

/**
 * Who owns the site, and the two ways out of the row.
 *
 * The name is the row's own control: it opens the scan report, and it carries
 * the overlay that makes the whole row clickable. That is new - the row used to
 * have no view of its own, and the name was a link to the register instead.
 *
 * The register did not lose its link, it moved onto the icon that was already
 * sitting there doing nothing. The gate is `regCode` and not `companyName` on
 * purpose: a name can also come from the page's own text, and that name has no
 * register row behind it. No match, no icon - and the row menu says the same
 * thing in words, for anyone who does not think to try a 14px building.
 *
 * The domain under it keeps pointing at the site itself. Three destinations,
 * three separate controls, and the two small ones lift above the overlay so a
 * click on them does not open the report instead.
 */
export function Company({ lead, onOpenScan, t }: { lead: ScanLead; onOpenScan: () => void; t: T }) {
  const name = lead.companyName ?? lead.domain;

  return (
    <span className="flex min-w-0 items-center gap-2.5">
      {lead.companyName ? (
        lead.regCode ? (
          <Link
            to={`/ariregister/${lead.regCode}`}
            title={`${t("leads.openRegister")} - ${lead.regCode}`}
            aria-label={t("leads.openRegister")}
            className="crm-mut relative z-[1] shrink-0 transition-colors hover:text-[var(--color-primary)]"
          >
            <Building2 className="size-3.5" />
          </Link>
        ) : (
          <Building2 className="crm-mut size-3.5 shrink-0" />
        )
      ) : null}
      <span className="min-w-0">
        <button
          type="button"
          onClick={onOpenScan}
          title={t("leads.openScan")}
          className="crm-prow-n crm-trunc block max-w-full text-left after:absolute after:inset-0 after:rounded-full"
        >
          {name}
        </button>
        <span className="flex min-w-0 items-center gap-1.5">
          <a
            href={lead.url}
            target="_blank"
            rel="noreferrer noopener"
            className="crm-prow-s crm-trunc relative z-[1] inline-flex items-center gap-1 hover:text-[var(--color-primary)] hover:underline"
          >
            {lead.domain}
            <ExternalLink className="size-3 shrink-0" />
          </a>
          {/* What the site is for, beside the address. A lead's severity already
              went through this fact - a portfolio one-pager is never called hot -
              so the row should say which fact it was. */}
          {lead.siteKind && lead.siteKind !== "unknown" ? (
            <StatusPill tone="mut">{t(`scanner.kind.${lead.siteKind}`)}</StatusPill>
          ) : null}
        </span>
      </span>
    </span>
  );
}
